import config from "api/config";
import {
  FeatureConfigurationLevel,
  useGetLettersExpansionConfiguration,
  LettersExpansionConfigurationResponse,
} from "@coherehealth/core-platform-api";
import { useEffect, useState } from "react";
import { UseMutateProps, UseMutateReturn } from "restful-react";
import { IGNORE_ERRORS, error as logError } from "../logger";

type GetPayorConfigByHealthPlanQueryParams = {
  featureConfigurationLevel: FeatureConfigurationLevel;
  healthPlanName: string;
};

interface FetchError {
  data?: string | FetchError;
  message?: string;
}

/**
 * Wrapper around useMutate so it can be used with the useGet API
 * For basic retrieval of payor config objects at the health plan level
 * @param useMutateHook The original hook from restful-react
 * @param healthPlanName The health plan name, from the service request
 * @param description Description of the config; used for logging
 */
export function useGetPayorConfigByHealthPlan<TData>(
  useMutateHook: (
    props: Omit<UseMutateProps<TData, unknown, GetPayorConfigByHealthPlanQueryParams, void, void>, "path" | "verb">
  ) => UseMutateReturn<TData, unknown, void, GetPayorConfigByHealthPlanQueryParams, void>,
  healthPlanName: string = "",
  description?: string
) {
  const queryParams: GetPayorConfigByHealthPlanQueryParams = {
    featureConfigurationLevel: "HEALTH_PLAN",
    healthPlanName,
  };

  const { mutate, error, loading } = useMutateHook({ queryParams, base: config.SERVICE_API_URL });
  const [data, setData] = useState<TData | null>(null);

  useEffect(() => {
    let unmounted = false;
    const fetchData = async () => {
      try {
        if (healthPlanName && !unmounted) {
          const config = await mutate();
          setData(config);
        }
      } catch (err) {
        if (!shouldIgnoreErrorEntry(err as FetchError)) {
          const error = err as FetchError;
          let errorMessage = `Could not fetch ${description || "payor config"}`;
          errorMessage += healthPlanName ? ` for ${healthPlanName}` : "";

          if (error.message) {
            errorMessage += ` message: ${error.message}`;
          }

          if (error.data) {
            errorMessage += ` data: ${typeof error.data === "string" ? error.data : JSON.stringify(error.data)}`;
          }

          logError(errorMessage);
        }
      }
    };
    fetchData();
    return () => {
      unmounted = true;
    };
  }, [healthPlanName, description, mutate]);

  return {
    data,
    error,
    loading,
  };
}

export function useGetLettersExpansionConfigurationByPayer(healthPlanName: string) {
  return useGetPayorConfigByHealthPlan<LettersExpansionConfigurationResponse>(
    useGetLettersExpansionConfiguration,
    healthPlanName,
    "letters expansion payor config"
  );
}

const shouldIgnoreErrorEntry = (error: FetchError, depth = 0): boolean => {
  const ignoreEntry = IGNORE_ERRORS.includes(error.message ?? "");
  if (!ignoreEntry && error.data && typeof error.data !== "string" && depth < 5) {
    return shouldIgnoreErrorEntry(error.data, depth + 1);
  }
  return ignoreEntry;
};
